import { LANGUAGE, SIDE } from "_constants";
import { ReactComponent as Rally } from "./assets/rally.svg";
import { ReactComponent as VolleyballPlayer } from "./assets/volleyball-player.svg";
import { ReactComponent as Timer } from "./assets/stopwatch.svg";
import { ReactComponent as TechnicalTimeout } from "./assets/technical-time-out.svg";
import { ReactComponent as Whistle } from "./assets/whistle.svg";
import { ComponentType } from "react";
import { MatchStatus } from "service";

const MESSAGES_EN: Record<string, string> = {
  "1248": "Serve",
  "2248": "Serve",
  "248": "Serve",
  "1249": "Rally",
  "249": "Rally",
  "1250": "Point Scored",
  "1254": "End of Set",
  "1260": "Point Scored",
  "1252": "Service fault", //
  "1253": "Ace", //
  "1255": "Timeout",
  "1256": "Technical Timeout",
  "1257": "Last Six",
  "1258": "Serve Points Won",
  "1259": "Current Streak",
  "1261": "End Of Match", //
  "9999": "Served",
};

const MESSAGES_ALB: Record<string, string> = {
  "1248": "Serve",
  "2248": "Serve",
  "248": "Serve",
  "249": "Rally",
  "1249": "Rally",
  "1250": "Point Scored",
  "1254": "End of Set",
  "1260": "Point Scored",
  "1252": "Service fault",
  "1253": "Ace",
  "1255": "Timeout",
  "1256": "Technical Timeout",
  "1257": "Last Six",
  "1258": "Serve Points Won",
  "1259": "Current Streak",
  "1261": "End Of Match",
  "9999": "Served",
};

export const MESSAGES = {
  [LANGUAGE.ENGLISH]: MESSAGES_EN,
  [LANGUAGE.ALBANIAN]: MESSAGES_ALB,
};

export const ICONS: Record<string, ComponentType<any>> = {
  "1248": VolleyballPlayer,
  "248": VolleyballPlayer,
  "1249": Rally,
  "249": Rally,
  "1250": VolleyballPlayer,
  "1252": VolleyballPlayer,
  "1253": VolleyballPlayer, // Need icon for Ace
  "1254": Whistle,
  "1255": Timer,
  "1256": TechnicalTimeout,
  "1261": Whistle,
  "9999": VolleyballPlayer,
  "?": Whistle, // Scambio
};

type Nullable<T> = T | null | undefined;
export type VolleyballCodeMeta = {
  normalizedCode?: string;
  baseCode?: string;
  side?: SIDE;
  lookupCodes: string[];
};
const digitsOnly = (value: string) => value.replace(/\D/g, "");
const collectCandidateCodes = (
  status: Nullable<MatchStatus>,
  fallbackNormalizedCode: Nullable<string>
) => {
  const codes: string[] = [];
  const append = (value: Nullable<string | number>) => {
    if (value === undefined || value === null) return;
    const str = String(value).trim();
    if (str) codes.push(str);
  };
  append(status?.sc);
  append((status as any)?.nsc);
  append(fallbackNormalizedCode);
  return codes
    .map((raw) => ({ raw, digits: digitsOnly(raw) }))
    .filter(({ digits }) => digits.length > 0);
};
export const getVolleyballCodeMeta = (
  status: Nullable<MatchStatus>,
  fallbackNormalizedCode?: Nullable<string>
): VolleyballCodeMeta => {
  const candidates = collectCandidateCodes(status, fallbackNormalizedCode);
  if (!candidates.length) {
    return { lookupCodes: [] };
  }
  const prioritized =
    candidates.find(({ digits }) => digits.length >= 4) ?? candidates[0];
  let normalizedDigits = prioritized.digits;
  if (normalizedDigits.length > 4) {
    normalizedDigits = normalizedDigits.slice(-4);
  } else if (normalizedDigits.length < 4) {
    const base = normalizedDigits.slice(-3);
    const prefix = normalizedDigits.charAt(0);
    if ((prefix === "1" || prefix === "2") && base) {
      normalizedDigits = `${prefix}${base}`;
    } else {
      normalizedDigits = base || normalizedDigits;
    }
  }
  const baseCode =
    normalizedDigits.length >= 3
      ? normalizedDigits.slice(-3)
      : normalizedDigits || undefined;
  const lookupSet = new Set<string>();
  if (normalizedDigits) lookupSet.add(normalizedDigits);
  if (baseCode) {
    lookupSet.add(baseCode);
    lookupSet.add(`1${baseCode}`);
    lookupSet.add(`2${baseCode}`);
  }
  let side: SIDE | undefined;
  const teamIndicator = normalizedDigits.charAt(0);
  if (teamIndicator === "1") side = SIDE.HOME;
  else if (teamIndicator === "2") side = SIDE.AWAY;
  return {
    normalizedCode: normalizedDigits || undefined,
    baseCode,
    side,
    lookupCodes: Array.from(lookupSet),
  };
};
